import { MathUtils, Vector3 } from "three/webgpu";
import {
  composeWorldPositionFromCartography,
  CARTOGRAPHY_SCALE,
  decomposeWorldPositionToCartography,
  projectCartographyPositionInto,
} from "../config/worldFrame";
import {
  gravitationalDistortionConfig,
  type GravitationalFieldProfileConfig,
} from "../config/gravitationalDistortionConfig";

const FIELD_EPSILON = 0.0001;

const projectionScratch = new Vector3();
const cartographyScratch = new Vector3();

export type GravitationalFieldProfile = "anchor" | "orbital" | "connector";

function fieldProfile(profile: GravitationalFieldProfile): GravitationalFieldProfileConfig {
  return gravitationalDistortionConfig.profiles[profile];
}

export function gravitationalFieldInfluence(planarRadius: number, profile: GravitationalFieldProfile = "anchor") {
  const active = fieldProfile(profile);
  const normalized = MathUtils.clamp(
    (active.outerRadius - planarRadius) / (active.outerRadius - active.innerRadius),
    0,
    1,
  );
  const smoothed = normalized * normalized * (3 - 2 * normalized);
  return Math.pow(smoothed, active.coreExponent);
}

export function distortWorldPosition(
  worldPosition: Vector3,
  target: Vector3,
  profile: GravitationalFieldProfile = "anchor",
) {
  const components = decomposeWorldPositionToCartography(worldPosition, cartographyScratch);
  const radius = Math.hypot(components.x, components.y) / CARTOGRAPHY_SCALE;
  if (radius < FIELD_EPSILON) return target.copy(worldPosition);

  const active = fieldProfile(profile);
  const influence = gravitationalFieldInfluence(radius, profile);
  if (influence < FIELD_EPSILON) return target.copy(worldPosition);

  const shiftedRadius = radius + influence * active.maxRadialShift;
  const scale = shiftedRadius / radius;
  const liftedDepth = components.z + influence * active.maxNormalShift;
  return composeWorldPositionFromCartography(components.x * scale, components.y * scale, liftedDepth, target);
}

export function projectDistortedCartographyPosition(
  position: readonly [number, number, number],
  target: Vector3,
  profile: GravitationalFieldProfile = "anchor",
) {
  return distortWorldPosition(projectCartographyPositionInto(position, projectionScratch), target, profile);
}
